<template>
  <list-layout
    :isChild="true"
    :title="$t('performer_lineups') | startcase"
    :count="userActionCounts.lineupConflictsCount"
    :apply-filters="applyFilters"
    :filters-pending="filtersPending"
    :reset-filters="resetFilters"
    :filters-applied="filtersApplied"
    :search-filter="filters.fuzzySearch"
    no-apply-btn
    no-search
  >
    <template v-slot:search>
      <ValidationObserver ref="fuzzySearchForm">
        <form-field
          class="file-search"
          :placeholder="$t('search') | capitalize"
          v-model="filters.fuzzySearch.value"
          :enter-action="applyFilters"
          :icon="['fas', 'search']"
          name="fuzzySearch"
        />
      </ValidationObserver>
    </template>

    <template v-slot:beforeFilters>
      <b-row>
        <b-col>
          <filter-builder
            :id="userPreferenceNames.PERFORMER_CONFLICTS_FILTER"
            :filters="filters"
            :filters-pending="filtersPending"
            :apply-filters="applyFilters"
            :reset-filters="resetFilters"
            :filters-applied="filtersApplied"
            @filter="onFilter"
          />
        </b-col>
      </b-row>
    </template>

    <template v-slot:counts>
      <pagination
        class="smaller p-0"
        :page="page"
        :count="count"
        :loading="loading"
        v-model="perPage"
        @limit="onLimit"
      />
    </template>

    <template v-slot:merge>
      <b-row align-h="end">
        <b-col cols="auto">
          <b-form-checkbox switch
            v-show="!permissions.actions.hasAllRhPermission"
            v-model="filters.financialInterest.value"
          >
            {{ $t("with_financial_interest") | capitalize }}
          </b-form-checkbox>
        </b-col>
        <b-col cols="auto">
          <b-form-checkbox v-model="actionStatus" switch>
            {{ $t("show_resolved") | capitalize }}
          </b-form-checkbox>
        </b-col>
      </b-row>
    </template>

    <b-card no-body v-for="(recording, index) in data" :key="index" :class="{ 'blink': shouldBlink(recording.id), 'd-none': isHidden(recording.id), 'active': isActive(index)}">
      <modal
        :id="`confirmation-modal-solved-${recording.id}`"
        :customTitle="$t('user_action_solved') | capitalize"
        modal-class="custom-modal edit"
        modalType="edit"
        size="md"
        centered
        hide-footer
        hide-header-close
        no-close-on-esc
        no-close-on-backdrop
      >
        <div>{{ $t("user_action_has_been_solved") | capitalize }}</div>
        <b-row class="float-right modal-footer mt-4">
          <b-button @click="blinkAndHide(recording.id, `conflict-${recording.id}`, getData)" class="btn-outline-red">
            {{ $t("accept") | capitalize }}
          </b-button>
        </b-row>
      </modal>
      <b-card-header class="first-level">
        <b-row align-v="center">
          <b-col cols="5" class="d-flex justify-content-start">
            <fa-icon
              v-if="isInProgressFrontend(recording.id) || (isInProgressBackend(recording.userActionsStatuses) && !isReleasedFrontend(recording.id))"
              :icon="['fa', 'user-edit']"
               color="red"
            />
            <collapse-button class="collapse-button mr-2" :toggle="`conflict-${recording.id}`" :expanded="expanded[index]" :disabled="loading" />
            {{ truncate(recording.title, { length: 100 }) }}
          </b-col>
          <b-col cols="7" class="d-flex justify-content-end">
            <b-row class="w-100 header-right">
              <b-col cols="2">
                <b-row>{{ $t('created_on') | capitalize }}:</b-row>
                <b-row>{{ parseDate(recording.userActionCreated, $config.DATE_FORMAT) }}</b-row>
              </b-col>
              <b-col cols="2">
                <b-row>{{ $t('playcount') | capitalize }}:</b-row>
                <b-row>{{ recording.playCount | formatNumber }}</b-row>
              </b-col>
              <b-col cols="2" class="custom-width">
                <b-row>{{ $t('weighted_playcount') | capitalize }}:</b-row>
                <b-row>{{ recording.weightedPlayCount | formatNumber }}</b-row>
              </b-col>
              <b-col cols="2">
                <b-row>{{ $t('royalties_paid') | capitalize }}:</b-row>
                <b-row>
                  <span class="smaller">
                    ${{ recording.royaltiesPaid | formatNumber(2) }}
                  </span>
                </b-row>
              </b-col>
              <b-col cols="2">
                <b-row>{{ $t('estimated_track_value') | capitalize }}:</b-row>
                <b-row>
                  <span class="smaller">
                    ${{ recording.estimatedTrackValue | formatNumber(2) }}
                  </span>
                </b-row>
              </b-col>
              <b-col cols="2">
                <status
                  v-if="(
                    isInProgressFrontend(recording.id) ||
                    (isInProgressBackend(recording.userActionsStatuses) && !isReleasedFrontend(recording.id))
                  ) && !isSolved(recording.id)"
                  :text="$t('in_progress') | capitalize"
                  :icon="['fas', 'question-circle']"
                  color="orange"
                />
                <status
                  v-else-if="(isPending(recording.userActionsStatuses) || isReleasedFrontend(recording.id)) && !isSolved(recording.id)"
                  :text="$t('conflict') | capitalize"
                  :icon="['fas', 'exclamation-circle']"
                  color="red"
                />
                <status
                  v-else
                  :text="$t('resolved') | capitalize"
                  :icon="['fas', 'check-circle']"
                  color="green"
                />
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </b-card-header>
      <b-collapse
        accordion="lineup-conflicts-list"
        :id="'conflict-' + recording.id"
        v-model="expanded[index]"
        role="tabpanel"
        @hide="onConflictClosed(recording)"
        @show="onConflictOpened(recording)"
        class="lineup-conflict-collapse"
      >
        <b-card-body v-if="expanded[index]">
          <div class="general-details">
            <b-table
              id="general-details"
              :items="[recording]"
              :fields="fields"
              fixed
              striped
            >
              <!-- Width handling -->
              <template v-slot:table-colgroup="scope">
                <col
                  v-for="field in scope.fields"
                  :key="field.key"
                  :style="{ width: field.width }"
                />
              </template>
              <!-- Genre Type -->
              <template v-slot:cell(genreType)="row">
                <b-form-select
                  v-if="editModePerformers"
                  size="sm"
                  v-model="genre"
                  :options="getFieldOptions('genreType')"
                ></b-form-select>
                <span v-else>
                  {{ getGenreType(row.item.genreType) }}
                </span>
              </template>
              <template v-slot:cell(resoundId)="row">
                <database-link :type="linkTypes.RECORDING" :item-id="row.item.id">{{ row.item.resoundId }}</database-link>
              </template>
            </b-table>
          </div>
          <performers
              :recordingUUID="recording.id"
              :genre-type="recording.genreType"
              :sub-genre="recording.subGenre"
              :categoryTypes="recording.categoryTypes"
              :solved-manually="solvedManually"
              :view-mode="viewMode"
              :show-highlights="true"
              :onDone="onDone"
              :resound-id="recording.resoundId"
          >
          </performers>
          <div>
            <h4>{{ $t("comments") | startcase }}</h4>
            <comments
              :createEndpoint="$api.comments.lineupConflictCommentCreate"
              :listEndpoint="$api.comments.lineupConflictCommentsList"
              :uuid="recording.id"
              :editing=false
              :deleting=false
            />
          </div>
        </b-card-body>
      </b-collapse>
    </b-card>

    <table-pagination
      class="mt-3"
      v-model="page"
      :total-rows="count"
      :per-page="perPage"
    />
  </list-layout>
</template>
<script>
import {
  UserActionTypeCodes,
  actionsTabs,
  conflictModes,
  filterTypes,
  genreTypes,
  getPerPageItemDefaults,
  linkTypes,
  userActionNotifications, userActionStatuses, userPreferenceNames
} from "@/constants"
import { capitalize, get, startCase, truncate } from "lodash"
import { getGenreType, getSubGenre } from "@/pages/Repertoire/Recordings/RecordingDetail/utils"
import { mapActions, mapGetters, mapMutations, mapState } from "vuex"
import DatabaseLink from "@/components/DatabaseLink.vue"
import FilterBuilder from "@/components/FilterBuilder"
import Pagination from "@/components/Pagination"
import { ValidationObserver } from "vee-validate"
import { listRouteMixin } from "@/utils/mixins"
import moment from "moment"
import parseDate from "@/utils/date-parser"
import { websocketsMixin } from "@/utils/wsmixin"

export default {
  name: "LineupConflictsList",
  mixins: [listRouteMixin, websocketsMixin],
  computed: {
    ...mapState("repertoire", ["editModePerformers", "performersUpdate"]),
    ...mapState("actionTabs", ["userActionCounts"]),
    ...mapGetters("repertoire", ["getFieldOptions"]),
    ...mapGetters("user", ["isInternal"]),
    ...mapState("user", ["permissions"]),
    viewMode: function () {
      if (!this.permissions.actionsTabs[actionsTabs.LINEUP_CONFLICTS].canWrite) {
        return conflictModes.VIEW
      }
      return this.actionStatus ? conflictModes.VIEW : conflictModes.SOLVE
    },
    actionStatus: {
      get () {
        return this.filters.status.value.length === 1 && this.filters.status.value[0] === userActionStatuses.DONE
      },
      set (value) {
        this.filters.status.value = value ? [userActionStatuses.DONE] : [userActionStatuses.PENDING, userActionStatuses.IN_PROGRESS]
      },
    },
    genre: {
      get () {
        let genre = ""
        if (this.currentRecording) {
          genre = this.currentRecording.genreType
          if ([genreTypes.CLASSICAL, genreTypes.JAZZ].includes(genre)) {
            genre = `${genre}_${this.currentRecording.subGenre}`
          }
        }
        return genre
      },
      set (value) {
        if ([genreTypes.CLASSICAL, genreTypes.JAZZ].includes(value[0])) {
          this.currentRecording.genreType = value[0]
          this.currentRecording.subGenre = value.substring(2)
        } else {
          this.currentRecording.genreType = value
          this.currentRecording.subGenre = null
        }
      }
    },
    // WS values
    shownUuidList () {
      return this.data.map(c => c.id)
    },
  },
  data () {
    return {
      linkTypes,
      currentRecording: null,
      loading: true,
      recordingData: {},
      userPreferenceNames,
      fields: [
        { key: "resoundId", label: startCase(this.$t("crescendo_id")), width: "20%" },
        { key: "title", label: startCase(this.$t("title")), width: "40%" },
        { key: "artist", label: startCase(this.$t("artist")), width: "20%" },
        { key: "isrc", label: startCase(this.$t("isrc")), width: "20%" },
        { key: "genreType", label: startCase(this.$t("genre")), width: "20%" },
      ],
      currentGenreType: "",
      count: 0,
      page: 1,
      data: [],
      expanded: [],
      perPage: Number(getPerPageItemDefaults(this.$route)),
      causeFiltersPending: true,
      filters: {
        fuzzySearch: { value: "", defaultValue: "" },
        status: { value: "", defaultValue: [userActionStatuses.PENDING, userActionStatuses.IN_PROGRESS], hidden: true },
        createdDateRange: { value: null, defaultValue: null, type: filterTypes.DATE_RANGE, format: "YYYY-MM-DD", label: capitalize(this.$t("created_on")), noWatch: true },
        userTimezone: { value: null, defaultValue: null, hidden: true },
        action_types: { value: "", defaultValue: [UserActionTypeCodes.RECORDING_LINEUP_CONFLICT], hidden: true },
        financialInterest: { value: true, defaultValue: true, hidden:true },
        playcount: { value: null, defaultValue: null, label: capitalize(this.$t("playcount")), type: filterTypes.NUMBER_RANGE_NO_SLIDER, singleBound: true },
        weightedPlaycount: { value: null, defaultValue: null, label: capitalize(this.$t("weighted_playcount")), type: filterTypes.NUMBER_RANGE_NO_SLIDER, singleBound: true },
        title: { value: null, defaultValue: null, type: filterTypes.TEXT, label: capitalize(this.$t("title")) },
        artist: { value: null, defaultValue: null, type: filterTypes.TEXT, label: capitalize(this.$t("artist")) },
        royaltiesPaid: { value: null, defaultValue: null, label: capitalize(this.$t("royalties_paid")), type: filterTypes.NUMBER_RANGE_NO_SLIDER, singleBound: true },
        estimatedTrackValue: { value: null, defaultValue: null, label: capitalize(this.$t("estimated_track_value")), type: filterTypes.NUMBER_RANGE_NO_SLIDER, singleBound: true },
        rightsHolderUuid: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT_SEARCH,
          label: capitalize(this.$t("rightsholder")),
          placeholder: capitalize(this.$t("search_rightsholder")),
          excludeName: "id",
          valueField: "resoundId",
          textField: "name",
          searchName: "search",
          listEndpoint: this.$api.rightsholders.rightsholdersList,
          noWatch: true,
        },
      },
      conflictModes,
      // WS values
      userActionNotification: userActionNotifications.LINEUP,
      storeStatusesKey: "wsLineupConflictStatuses",
    }
  },
  components: {
    DatabaseLink,
    FilterBuilder,
    Performers: () => import("../Repertoire/Recordings/RecordingDetail/Performers/Performers"),
    Pagination,
    ValidationObserver,
  },
  watch: {
    page (value) {
      this.onPagination(value)
      this.getData()
    },
    perPage () {
      if (this.page === 1) {
        this.getData()
      } else {
        this.page = 1
      }
    },
  },
  methods: {
    ...mapActions("userActions", ["notifyUserAction"]),
    ...mapMutations("actionTabs", ["setUserActionCounts"]),
    ...mapMutations("alert", ["success", "error"]),
    getGenreType,
    truncate,
    onDone () {
      this.setUserActionCounts({
        ...this.userActionCounts,
        lineupConflictsCount: --this.userActionCounts.lineupConflictsCount
      })
      this.getData()
    },
    responseAdapter (resp) {
      if (resp) {
        this.data = resp.data.results
        this.count = resp.data.count
      }
      this.loading = false
    },
    onFilter (name,value) {
      if (name === "playcount") {
        this.filters.playcount.value = value
      } else if (name === "weighted_playcount") {
        this.filters.weightedPlaycount.value = value
      } else if (name === "rightsHolderUuid") {
        if (value) {
          this.changeFilter(name, value.id)
          this.changeFilter("rightsholder_rs_id", value.resoundId)
        } else {
          this.changeFilter(name, null)
          this.changeFilter("rightsholder_rs_id", null)
        }
        if (!this.causeFiltersPending) {
          this.filtersPending = false
          this.causeFiltersPending = true
        }
      } else if (name === "createdDateRange") {
        const range = (value) ?
          [
            this.parseDate(value[0], this.$config.ISO_DATE_FORMAT),
            this.parseDate(value[1], this.$config.ISO_DATE_FORMAT)
          ] : []
        if (range.length) {
          this.filters.userTimezone.value = Intl.DateTimeFormat().resolvedOptions().timeZone
        } else {
          this.filters.userTimezone.value = this.filters.userTimezone.defaultValue
        }
        this.changeFilter("from", range[0])
        this.changeFilter("to", range[1])
      }
    },
    getData () {
      this.loading = true
      this.$api.actions.userActionsLineupConflicts({
        ...{ "financial_interest": this.filters.financialInterest.value },
        ...this.$route.query,
      })
        .then((resp) => {
          this.responseAdapter(resp)
        })
        .catch((e) => {
          if (e.response.data.fuzzySearch) {
            this.error(capitalize(this.$t("wrong_filters")))
          }
          this.$refs.fuzzySearchForm.setErrors(e.response.data)
        })
        .finally(() => {
          this.expanded = []
        })
    },
    isActive (index) {
      return this.expanded[index]
    },
    onConflictOpened (recording) {
      this.currentRecording = recording
      this.onOpened(recording.id)
    },
    onConflictClosed (recording) {
      this.currentRecording = null
      this.onClosed(recording.id)
    },
    parseDate,
    getGenre (recording) {
      let genre = getGenreType(recording.genreType)
      if ([genreTypes.CLASSICAL, genreTypes.JAZZ].includes(recording.genreType)) {
        genre = `${genre} (${getSubGenre(recording.subGenre)})`
      }
      return genre
    }
  },
  mounted () {
    this.getData()
    this.page = Number(get(this.$route.query, "page", 1))
    this.perPage = Number(get(this.$route.query, "limit", this.perPage))
    let rightsHolderId = get(this.$route.query, "rightsholder_rs_id")
    if (rightsHolderId) {
      this.causeFiltersPending = false
      this.filters.rightsHolderUuid.searchValue = rightsHolderId
    }
    let createdStartDate = get(this.$route.query, "from")
    let createdEndDate = get(this.$route.query, "to")
    if (createdStartDate && createdEndDate) {
      this.filters.createdDateRange.value = [
        moment(createdStartDate, this.$data.filters.createdDateRange.format, true).toDate(),
        moment(createdEndDate, this.$data.filters.createdDateRange.format, true).toDate(),
      ]
    }
  },
}
</script>
<style lang="scss" scoped>
  .card {
    margin-bottom: 10px;
  }
  ::v-deep .smaller {
    font-size: 10pt;
  }

  .custom-width {
    max-width: min-content;
  }

  .header-right {
    .row {
      padding: 0 15px;
    }
  }

  .card-header {
   &.first-level {
    background-color: $row-select;
    border-bottom: none;
    }
  }

  .active {
    &.card {
      border: 1px solid #273C84 !important;
      .card-header {
        background-color: #E7EBF8;
      }
    }
  }

  .lineup-conflict-collapse {
    .general-details {
      border: 1px solid $table-header;
      border-radius: 4px;
      .b-table {
        margin-bottom: 0;
      }
      margin-bottom: 15px;
    }
  }
</style>
